import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import { dogBreedList } from '../../../helpers/dogHelper';
import dogTeamsInfo from '../../../helpers/dogTeamInfoHelper';
import './AddDogModal.css';
import { runSecurityTests } from '../../../utils/securityTest';

function AddDogModal({ onCancel, userId, setRefreshTrigger }) {
  const [name, setName] = useState('');
  const [breed, setBreed] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [birthday, setBirthday] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [errors, setErrors] = useState({});
  const overlayRef = useRef(null);
  const birthdayInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const breedInputRef = useRef(null);
  const teamContainerRef = useRef(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setTimeout(() => {
        scrollToError();
      }, 100);
    }
  }, [errors]);

  const handleClickOutside = event => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      onCancel();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBreedChange = (e) => {
    const userInput = e.target.value;
    setBreed(userInput);

    if (userInput.length > 0) {
      const lowerInput = userInput.toLowerCase();
      const matchedBreeds = dogBreedList.filter((breed) =>
        breed.toLowerCase().includes(lowerInput)
      );
      setSuggestions(matchedBreeds);
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (breed) => {
    setBreed(breed);
    setSuggestions([]);
  };

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    if (team && overlayRef.current) {
      setTimeout(() => {
        overlayRef.current.scrollTo({
          top: teamContainerRef.current.offsetTop - 100,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Trim and validate inputs
    const trimmedName = name.trim();
    const trimmedBreed = breed.trim();
    
    if (!trimmedName) {
      newErrors.name = 'Please enter a name for your dog';
    }
    
    if (!trimmedBreed) {
      newErrors.breed = 'Please enter a breed for your dog';
    }
    
    if (!birthday) {
      newErrors.birthday = 'Please select your dog\'s birthday';
    } else {
      const selectedDate = new Date(birthday);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        newErrors.birthday = 'Birthday cannot be in the future';
      }
    }
    
    if (!selectedTeam) {
      newErrors.team = 'Please select a team for your dog';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const scrollToError = () => {
    // Get the first error field
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) return;

    let elementToScroll;
    switch (firstErrorField) {
      case 'name':
        elementToScroll = nameInputRef.current;
        break;
      case 'breed':
        elementToScroll = breedInputRef.current;
        break;
      case 'birthday':
        elementToScroll = birthdayInputRef.current;
        break;
      case 'team':
        elementToScroll = teamContainerRef.current;
        break;
      default:
        return;
    }

    if (elementToScroll && overlayRef.current) {
      overlayRef.current.scrollTo({
        top: elementToScroll.offsetTop - 200, // Increased padding to show the entire date field
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return; // The useEffect will handle scrolling
    }

    // Run security tests
    const securityResults = runSecurityTests(name + breed);
    if (Object.values(securityResults).some(result => result)) {
      setErrors({
        general: 'Invalid input detected'
      });
      return;
    }

    try {
      const response = await axiosInstance.post(`/dogs`, {
        name: name.trim(),
        breed: breed.trim(),
        birthday,
        team: selectedTeam.id
      });
      
      if (response.status === 201) {
        setRefreshTrigger(prev => !prev);
        onCancel();
      }
    } catch (error) {
      setErrors({
        general: error.response?.data?.message || 'Failed to add dog. Please try again.'
      });
      // Scroll to top for general errors
      if (overlayRef.current) {
        overlayRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <div className="overlay">
      <div ref={overlayRef} className="add-dog-modal-container">
        <form onSubmit={handleSubmit} className="add-dog-modal-form">
          <div className="add-dog-modal-title">Welcome to the Pack!</div>
          <button className="add-dog-modal-close" onClick={onCancel}>X</button>
          
          {errors.general && (
            <div className="add-dog-modal-error">{errors.general}</div>
          )}

          <label className="add-dog-modal-label">What's your dog's name?</label>
          <input 
            ref={nameInputRef}
            className={`add-dog-modal-input ${errors.name ? 'error' : ''}`} 
            type="text" 
            value={name} 
            onChange={e => {
              setName(e.target.value);
              if (errors.name) setErrors(prev => ({ ...prev, name: null }));
            }} 
          />
          {errors.name && <div className="add-dog-modal-error">{errors.name}</div>}

          <label className="add-dog-modal-label">
            What breed is your dog?
            <br />
            If you don't see it, feel free to type it in &mdash; mixed breeds and fun descriptions are welcome!
          </label>
          <input 
            ref={breedInputRef}
            className={`add-dog-modal-input breed ${errors.breed ? 'error' : ''}`} 
            type="text" 
            value={breed} 
            onChange={handleBreedChange} 
          />
          {errors.breed && <div className="add-dog-modal-error">{errors.breed}</div>}
          {suggestions.length > 0 && (
            <ul className="add-dog-modal-breed-suggestions">
              {suggestions.map(breed => (
                <li key={breed} onClick={() => handleSelectSuggestion(breed)}>{breed}</li>
              ))}
            </ul>
          )}

          <label className="add-dog-modal-label">When is your dog's birthday?</label>
          <input 
            ref={birthdayInputRef}
            className={`add-dog-modal-input ${errors.birthday ? 'error' : ''}`} 
            type="date" 
            value={birthday} 
            onChange={e => {
              setBirthday(e.target.value);
              if (errors.birthday) setErrors(prev => ({ ...prev, birthday: null }));
            }} 
          />
          {errors.birthday && <div className="add-dog-modal-error">{errors.birthday}</div>}

          <div className="add-dog-modal-label" style={{marginTop: '1em', marginBottom: '1em', fontSize: '0.9em', fontStyle: 'italic'}}>
            After adding your dog, you'll be able to manage their vaccination records through the "Manage Vaccinations" feature. We require Rabies and DAP vaccinations to be up to date.
          </div>

          <div className="add-dog-modal-team-section-header">
            <div className="add-dog-modal-team-section-title">Pick your dog's team!</div>
          </div>
          <div ref={teamContainerRef} className="team-pick-container">
            {dogTeamsInfo.map((team) => (
              <div key={team.id} className="team-pick" onClick={() => handleSelectTeam(team)}>
                <img src={team.logo} alt={team.name} 
                  style={selectedTeam && selectedTeam.id === team.id ? {"box-shadow": `0 0 1.25em 0.3em ${team.color}`} : {}}
                />
              </div>
            ))}
          </div>
          {errors.team && <div className="add-dog-modal-error">{errors.team}</div>}

          {selectedTeam && (
              <div className="add-dog-modal-selected-team-container" style={{border: `0.25em solid `+selectedTeam.color, "border-radius": '2em', "position": "relative"}}>
                  <button className="add-dog-modal-selected-team-close-button" onClick={() => handleSelectTeam(null)}>X</button>
                  <div className="add-dog-modal-team-name" style={{color: selectedTeam.color}}>{selectedTeam.name}:</div>
                  <div className="add-dog-modal-team-name" style={{color: selectedTeam.color}}>{selectedTeam.teamName}</div>
                  <img src={selectedTeam.logo} alt={selectedTeam.name} className="add-dog-modal-selected-team-logo" style={{"box-shadow": `0 0 1.25em 0.3em `+selectedTeam.color}}/>
                  <div className="add-dog-modal-team-details">
                      <div className="add-dog-modal-team-description">{selectedTeam.description}</div>
                  </div>
              </div>
          )}

          <div className="add-dog-modal-button-container">
            <button type="submit" className="add-dog-modal-submit-button">Join the Pack!</button>
            <button type="button" className="add-dog-modal-cancel-button" onClick={onCancel}>Not Now</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddDogModal;
